import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Text,
  useToast
} from "@chakra-ui/react";
import Button from "components/Button/Button";
import SendCodeInput from "components/Input/SendCodeInput";
import { useEffect, useRef, useState } from "react";
import Recaptcha from "react-google-recaptcha";
import { post } from "util/axiosLib";
import { convertNumberMS } from "util/convertNumberToTime";
import { showLastThreeChar } from "util/hideInfo";

const PhoneVerification = ({hidePhoneText, onComplete, enableRecaptcha,updateOnRequest, verification, profile, token, data }) => {
  const PHONE_URL = `${process.env.REACT_APP_API_ENDPOINT}/auth/forgot2FA`;
  const securityAnswer = data;
  const recaptchaInstanceRef = useRef();
  const toast = useToast();
  const [phoneCode, setPhoneCode] = useState("");
  const [showPhoneText, setShowPhoneText] = useState(!hidePhoneText);
  const [btnLoading, setBtnLoading] = useState(false);
  const initialTimeRef = useRef();
  const [initialTime, setInitialTime] = useState(0);
  const [startTimer, setStartTimer] = useState(false);
  const [disableRequest, setDisableRequest] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  const headers = {
    authorization: token,
    "Content-Type": "application/json",
  };

  const requestCode = async (e) => {
    e.preventDefault();

    if(showPhoneText) setShowPhoneText(false);

    if (navigator.onLine === true) {
      let captcha = "";
      if(enableRecaptcha) {
        captcha = await recaptchaInstanceRef.current?.executeAsync();
      }
      setIsRequesting(true);

      try {
        let urlString = PHONE_URL;
        if(enableRecaptcha) urlString += `?humanKey=${captcha}`
        const response = await post(`${urlString}`,{securityAnswer}, {
          ...headers,
        });

        if (response.data.status === "success") {
          const iAlreadyProcessing = response.data.message.includes("already");
          if (iAlreadyProcessing && response.data?.data?.nextRequestInSecs) {
            initialTimeRef.current && clearTimeout(initialTimeRef.current);
            setInitialTime(response.data?.data?.nextRequestInSecs);
          } else {
            initialTimeRef.current && clearTimeout(initialTimeRef.current);
            setInitialTime(600);
          }
          setDisableRequest(true);
          setStartTimer(true);
          updateOnRequest && updateOnRequest(response.data, null)

          if(enableRecaptcha) await recaptchaInstanceRef.current?.reset();
          let errmsg = response.data.message;
          errmsg = typeof errmsg === "string" ? errmsg : "Something went wrong. Please try again";

          toast({
            position: "bottom-right",
            description: errmsg,
            status: iAlreadyProcessing ? "error" : "success",
            duration: 9000,
            isClosable: true,
          });
        }

        if (response.data.status === 202) {
          if(enableRecaptcha) await recaptchaInstanceRef.current?.reset();
          toast({
            position: "bottom-right",
            description: response.data.message,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      } catch (error) {
        let errmsg = error.customError;
        errmsg = typeof errmsg === "string" ? errmsg : "Something went wrong. Please try again";
        updateOnRequest && updateOnRequest(error, "Something went wrong")
        if(enableRecaptcha) await recaptchaInstanceRef.current?.reset();
        toast({
          position: "bottom-right",
          title: "last error",
          description: errmsg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      setIsRequesting(false);
    } else {
      updateOnRequest && updateOnRequest(null, "Please check your network connecting")
      toast({
        position: "bottom-right",
        title: "Network Error!!",
        description: "Please check your network connection",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handleSubmitPhoneCode = async (e) => {
    e.preventDefault();

    setBtnLoading(true);

    if (navigator.onLine === true) {
      let captcha = "";
      if(enableRecaptcha) {
        captcha = await recaptchaInstanceRef.current?.executeAsync();
      }

      try {
        let urlString = PHONE_URL;
        if(enableRecaptcha) urlString += `?humanKey=${captcha}`
        const response = await post(
          urlString,
          { code: phoneCode, securityAnswer },
          headers
        );

        if (response.status === 200 || response.status === 201) {
          setBtnLoading(false);
          onComplete && onComplete()
          if(enableRecaptcha) await recaptchaInstanceRef.current?.reset();
        }
      } catch (error) {
        if(enableRecaptcha) await recaptchaInstanceRef.current?.reset();
        setBtnLoading(false);
        toast({
          position: "bottom-right",
          description: error?.error || "",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    return () => {
      initialTimeRef.current && clearTimeout(initialTimeRef.current);
    };
  }, []);

  useEffect(() => {
    if (initialTime > 0) {
      initialTimeRef.current = setTimeout(() => {
        setInitialTime(initialTime - 1);
      }, 1000);
    }

    if (initialTime === 0 && startTimer) {
      setStartTimer(false);
      setDisableRequest(false);
    }
  }, [initialTime, startTimer, verification]);

  return (
    <Container maxW="lg">
      <>
        <Box as="form" mt="50px" w="100%">
            <FormControl w="100%">
              {!showPhoneText ? (
                <Text></Text>
              ) : (
                <Text as="cite" color="gray.500" mb="1rem" textAlign="left">
                  Code will be sent to{" "}
                  {`*********${showLastThreeChar(
                    profile?.phone && profile.phone
                  )}`}
                </Text>
              )}

              <FormLabel>One Time Code</FormLabel>
              <SendCodeInput
                minW="100%"
                timer={convertNumberMS(initialTime)}
                loading={isRequesting}
                placeholder="Reset confirmation code"
                onClick={requestCode}
                disabled={disableRequest}
                onChange={(e) => setPhoneCode(e.target.value)}
              />

              <Button
                minH="40px"
                fontWeight="normal"
                mt="30px !important"
                type="submit"
                isLoading={btnLoading}
                disabled={!(phoneCode.length === 6)}
                onClick={handleSubmitPhoneCode}>
                Submit
              </Button>
            </FormControl>
        </Box>
      </>

      {enableRecaptcha && 
        <Recaptcha
          ref={recaptchaInstanceRef}
          sitekey={process.env.REACT_APP_RECAPTCHA}
          size={"invisible"}
        />}
    </Container>
  );
};

export default PhoneVerification;
